import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Button, 
  Box, 
  Typography,
  IconButton,
  Tooltip,
  TextField,
  useTheme as useMuiTheme
} from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import PaletteIcon from '@mui/icons-material/Palette';
import ColorLensIcon from '@mui/icons-material/ColorLens';

interface ThemeCustomizerProps {
  open: boolean;
  onClose: () => void;
}

// Predefined color options
const primaryColorOptions = ['#1976d2', '#2e7d32', '#d32f2f', '#ed6c02', '#9c27b0'];
const secondaryColorOptions = ['#9c27b0', '#0288d1', '#ed6c02', '#2e7d32', '#d32f2f'];

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ open, onClose }) => {
  const { colors, setColors } = useTheme();
  const muiTheme = useMuiTheme();
  
  const [primaryColor, setPrimaryColor] = useState(colors.primary);
  const [secondaryColor, setSecondaryColor] = useState(colors.secondary);

  const handleSave = () => {
    // Only save valid colors
    if (isValidHex(primaryColor) && isValidHex(secondaryColor)) {
      console.log('Saving colors:', { primary: primaryColor, secondary: secondaryColor });
      setColors({ primary: primaryColor, secondary: secondaryColor });
      onClose();
    } else {
      alert('Please enter valid hex colors before saving');
    }
  };

  const handleReset = () => {
    setPrimaryColor('#1976d2');
    setSecondaryColor('#9c27b0');
  };

  // Function to validate hex color
  const isValidHex = (color: string) => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
  };

  // Handle manual color input
  const handlePrimaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (newColor.startsWith('#') && newColor.length <= 7) {
      setPrimaryColor(newColor);
    } else if (newColor === '') {
      setPrimaryColor('#');
    }
  };

  const handleSecondaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (newColor.startsWith('#') && newColor.length <= 7) {
      setSecondaryColor(newColor);
    } else if (newColor === '') {
      setSecondaryColor('#');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: { 
          width: '100%', 
          maxWidth: 500,
          bgcolor: muiTheme.palette.background.paper
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: '"Plus Jakarta Sans", sans-serif'
      }}>
        Customize Button Colors
        <Tooltip title="Reset to defaults">
          <IconButton onClick={handleReset} size="small">
            <PaletteIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            Primary Button Color
          </Typography>
          
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField 
              label="Hex Color" 
              value={primaryColor} 
              onChange={handlePrimaryColorChange}
              error={!isValidHex(primaryColor) && primaryColor !== '#'}
              helperText={!isValidHex(primaryColor) && primaryColor !== '#' ? "Invalid hex color" : ""}
              size="small"
              sx={{ width: 150 }}
            />
            <Box 
              sx={{ 
                width: 40, 
                height: 40, 
                bgcolor: isValidHex(primaryColor) ? primaryColor : '#ccc',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }} 
            />
          </Box>
          
          <Typography variant="body2" sx={{ mb: 1 }}>Preset Colors:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {primaryColorOptions.map(color => (
              <Tooltip key={color} title={color}>
                <Box 
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: color, 
                    cursor: 'pointer',
                    border: primaryColor === color ? '3px solid black' : '1px solid #ccc',
                    borderRadius: '4px',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  onClick={() => setPrimaryColor(color)}
                />
              </Tooltip>
            ))}
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Preview:
            </Typography>
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: isValidHex(primaryColor) ? primaryColor : '#ccc', 
                '&:hover': { 
                  bgcolor: isValidHex(primaryColor) ? primaryColor : '#ccc', 
                  filter: 'brightness(0.9)' 
                } 
              }}
            >
              Primary Button
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            Secondary Button Color
          </Typography>
          
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField 
              label="Hex Color" 
              value={secondaryColor} 
              onChange={handleSecondaryColorChange}
              error={!isValidHex(secondaryColor) && secondaryColor !== '#'}
              helperText={!isValidHex(secondaryColor) && secondaryColor !== '#' ? "Invalid hex color" : ""}
              size="small"
              sx={{ width: 150 }}
            />
            <Box 
              sx={{ 
                width: 40, 
                height: 40, 
                bgcolor: isValidHex(secondaryColor) ? secondaryColor : '#ccc',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }} 
            />
          </Box>
          
          <Typography variant="body2" sx={{ mb: 1 }}>Preset Colors:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {secondaryColorOptions.map(color => (
              <Tooltip key={color} title={color}>
                <Box 
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: color, 
                    cursor: 'pointer',
                    border: secondaryColor === color ? '3px solid black' : '1px solid #ccc',
                    borderRadius: '4px',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  onClick={() => setSecondaryColor(color)}
                />
              </Tooltip>
            ))}
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              Preview:
            </Typography>
            <Button 
              variant="contained" 
              color="secondary"
              sx={{ 
                bgcolor: isValidHex(secondaryColor) ? secondaryColor : '#ccc', 
                '&:hover': { 
                  bgcolor: isValidHex(secondaryColor) ? secondaryColor : '#ccc', 
                  filter: 'brightness(0.9)' 
                } 
              }}
            >
              Secondary Button
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            variant="outlined" 
            color="error"
            onClick={() => {
              // Reset to Material UI defaults and save immediately
              const defaultColors = { primary: '#1976d2', secondary: '#9c27b0' };
              setColors(defaultColors);
              setPrimaryColor(defaultColors.primary);
              setSecondaryColor(defaultColors.secondary);
            }}
          >
            Reset to Default
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined">Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ 
                bgcolor: isValidHex(primaryColor) ? primaryColor : '#ccc', 
                '&:hover': { 
                  bgcolor: isValidHex(primaryColor) ? primaryColor : '#ccc', 
                  filter: 'brightness(0.9)' 
                } 
              }}
            >
              Save Colors
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Standalone button to open the customizer
export const ThemeCustomizerButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  
  return (
    <>
      <Tooltip title="Customize Colors">
        <IconButton 
          onClick={() => setOpen(true)} 
          size="small" 
          color="inherit"
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>
      
      <ThemeCustomizer 
        open={open} 
        onClose={() => setOpen(false)} 
      />
    </>
  );
};

export default ThemeCustomizer;
